import React, { useEffect, useMemo, useState, useRef } from 'react';
import clsx from 'clsx';
import SvgIcon from '../../SvgIcon/SvgIcon';
import Checkbox from '../../Forms/Input/Checkbox';
import useDebounced from '../../../hooks/useDebounced';
import {
  DateFilterField,
  NumberFilterField,
  SearchSelectFilterField,
  SelectFilterField,
  TextFilterField,
} from './Filters/fields';
import { FilterConfig, FilterType } from './Filters/types';
import { getFilterColSpan } from '../../../utils/responsive/filterBarResponsiveConfig'; // 👈 util responsiva

interface FilterBarProps {
  filtersConfig: FilterConfig[];
  onFilterChange?: (filters: Record<string, string>) => void;
  onResetFilters?: () => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  filtersConfig,
  onFilterChange,
  onResetFilters,
}) => {
  const skipNextEffectRef = useRef(false);

  const initialValues = filtersConfig.reduce((acc, curr) => {
    acc[curr.name] = '';
    return acc;
  }, {} as Record<string, string>);

  const [filters, setFilters] = useState(initialValues);
  const [lastSentFilters, setLastSentFilters] = useState<Record<string, any>>(
    {}
  );
  const debouncedFilters = useDebounced(filters, 700);

  const filterTypesMap = useMemo(() => {
    return filtersConfig.reduce((acc, curr) => {
      acc[curr.name] = curr.type;
      return acc;
    }, {} as Record<string, FilterType>);
  }, [filtersConfig]);

  useEffect(() => {
    if (skipNextEffectRef.current) {
      skipNextEffectRef.current = false;
      return;
    }

    const hasFilter = Object.values(debouncedFilters).some(
      (val) => val !== '' && val !== null
    );
    if (!hasFilter) return;

    const parsedFilters: Record<string, any> = { ...debouncedFilters };

    Object.keys(parsedFilters).forEach((key) => {
      const type = filterTypesMap[key];
      const value = parsedFilters[key];

      // -----------------------------------------
      // 🔢 NUMBER
      // -----------------------------------------
      if (type === 'number') {
        if (value !== '' && value !== null) {
          parsedFilters[key] = Number(value);
        } else {
          delete parsedFilters[key];
        }
      }

      // -----------------------------------------
      // 🔽 SELECT / SEARCHSELECT
      // Detecta si el ID es number o string
      // -----------------------------------------
      if (type === 'select' || type === 'searchSelect') {
        if (value !== '' && value !== null) {
          const option = filtersConfig
            .find((f) => f.name === key)
            ?.options?.find((o) => o.id == value);

          if (option) {
            // Si el option.id es número → convertir a número
            // Si es string → mantener string
            parsedFilters[key] =
              typeof option.id === 'number' ? Number(value) : String(value);
          } else {
            parsedFilters[key] = value; // fallback seguro
          }
        } else {
          delete parsedFilters[key];
        }
      }

      // -----------------------------------------
      // 📅 DATE → YYYY-MM-DD
      // -----------------------------------------
      if (type === 'date') {
        if (value) {
          parsedFilters[key] =
            value instanceof Date ? value.toISOString().split('T')[0] : value;
        } else {
          delete parsedFilters[key];
        }
      }

      // -----------------------------------------
      // 📝 TEXT
      // -----------------------------------------
      if (type === 'text') {
        if (value === '' || value === null) {
          delete parsedFilters[key];
        }
      }

      if (type === 'checkbox') {
        if (value === 'true') parsedFilters[key] = true;
        else delete parsedFilters[key];
      }
    });

    const isEqual =
      JSON.stringify(parsedFilters) === JSON.stringify(lastSentFilters);

    if (!isEqual) {
      onFilterChange?.(parsedFilters);
      setLastSentFilters(parsedFilters);
    }
  }, [debouncedFilters, filterTypesMap]);

  const resetFilters = () => {
    skipNextEffectRef.current = true;
    setFilters(initialValues);
    setLastSentFilters({});
    onResetFilters?.();
  };

  const renderField = (filter: FilterConfig) => {
    const { name, label, type, options } = filter;
    const value = filters[name];

    const commonProps = {
      name,
      label,
      value,
      onChange: (val: string) => {
        setFilters((prev) => ({ ...prev, [name]: val }));
      },
    };

    switch (type) {
      case 'text':
        return <TextFilterField key={name} {...commonProps} />;
      case 'number':
        return <NumberFilterField key={name} {...commonProps} />;
      case 'select':
        return (
          <SelectFilterField
            key={name}
            {...commonProps}
            options={options || []}
          />
        );
      case 'searchSelect':
        return (
          <SearchSelectFilterField
            key={name}
            {...commonProps}
            options={options || []}
          />
        );
      case 'date':
        return <DateFilterField key={name} {...commonProps} />;
      case 'checkbox':
        return (
          <div key={name} className="flex h-full items-center">
            <Checkbox
              id={name}
              label={label}
              checked={value === 'true'}
              onChange={() =>
                commonProps.onChange(value === 'true' ? 'false' : 'true')
              }
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="px-4 pt-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-12">
        {filtersConfig.map((filter) => (
          <div
            key={filter.name}
            className={clsx('w-full', getFilterColSpan(filter.colSpan))}
          >
            {renderField(filter)}
          </div>
        ))}
      </div>

      <div className="mt-4 flex justify-end px-2">
        <button
          type="button"
          onClick={resetFilters}
          className="inline-flex items-center gap-2 rounded bg-primary px-4.5 py-2.5 text-sm font-medium text-white hover:bg-opacity-90"
        >
          <SvgIcon name="continue" />
          Restablecer filtros
        </button>
      </div>
    </div>
  );
};

export default FilterBar;
export type { FilterConfig };
